import * as React from "react";
import * as yup from "yup";
import { Field, Formik, FormikProps } from "formik";
import { BaseModal } from "../../../atoms/base-modal";
import classnames from "classnames";
import { Organization } from "../../../../application/domain/organization/organization";

interface ModalWithInputProps {
  isLoading: boolean;
  title: string;
  isActive: boolean;
  onSubmit: (org: Organization) => void;
  onModal: () => void;
  buttonText: string;
}

const REQUIRED_ERROR = "必須項目です。";
const MAX_LENGTH_ERROR = "最大入力文字数を超えています。";
const SLUG_REGEX_ERROR = "半角英数字以外の文字が入力されています。";

export const OrganizationBasicInfoCreateModal: React.FunctionComponent<
  ModalWithInputProps
> = ({ isLoading, title, isActive, onModal, onSubmit, buttonText }) => {
  const initialOrg: Organization = {
    name: "",
    localGovCode: "",
    slug: "",
    officialWebSiteURL: "",
    siteName: "",
    logoURL: "",
  };
  return (
    <BaseModal isActive={isActive} title={title} onClose={onModal} hasOverflow>
      <Formik
        validateOnChange={true}
        initialValues={initialOrg}
        validateOnMount={true}
        onSubmit={(v) => {
          onSubmit(v);
          onModal();
        }}
        validationSchema={yup.object().shape({
          name: yup.string().required(REQUIRED_ERROR).max(20, MAX_LENGTH_ERROR),
          localGovCode: yup.string().required(REQUIRED_ERROR),
          slug: yup
            .string()
            .required(REQUIRED_ERROR)
            .matches(/^[0-9a-zA-Z-]+$/, SLUG_REGEX_ERROR),
          officialWebSiteURL: yup
            .string()
            .required(REQUIRED_ERROR)
            .max(255, MAX_LENGTH_ERROR),
          siteName: yup
            .string()
            .required(REQUIRED_ERROR)
            .max(16, MAX_LENGTH_ERROR),
          logoURL: yup.string().max(255, MAX_LENGTH_ERROR),
        })}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isValid,
        }: FormikProps<Organization>) => (
          <form onSubmit={handleSubmit}>
            <section className="modal-card-body">
              <div className="field">
                <label className="label">テナント正式名称(必須)</label>
                <Field
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Graffer市"
                  type="text"
                  className={classnames("input", {
                    "is-danger": errors.name && touched.name,
                  })}
                />
                {touched.name && errors.name && (
                  <p className="is-size-7 has-text-danger">{errors.name}</p>
                )}
              </div>
              <div className="field">
                <label className="label">
                  地方団体コード(必須)
                  <p className="help has-text-weight-normal">
                    一度登録すると後から変更できません。
                  </p>
                </label>
                <Field
                  name="localGovCode"
                  value={values.localGovCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="99999"
                  type="text"
                  className={classnames("input", {
                    "is-danger": errors.localGovCode && touched.localGovCode,
                  })}
                />
                {touched.localGovCode && errors.localGovCode && (
                  <p className="is-size-7 has-text-danger">
                    {errors.localGovCode}
                  </p>
                )}
              </div>
              <div className="field">
                <label className="label">
                  スラグ(必須)
                  <p className="help has-text-weight-normal">
                    一度登録すると後から変更できません。
                  </p>
                </label>
                <Field
                  name="slug"
                  value={values.slug}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="city-graffer"
                  type="text"
                  className={classnames("input", {
                    "is-danger": errors.slug && touched.slug,
                  })}
                />
                {touched.slug && errors.slug && (
                  <p className="is-size-7 has-text-danger">{errors.slug}</p>
                )}
              </div>
              <div className="field">
                <label className="label">公式サイトURL(必須)</label>
                <Field
                  name="officialWebSiteURL"
                  value={values.officialWebSiteURL}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="https://graffer.jp"
                  type="text"
                  className={classnames("input", {
                    "is-danger":
                      errors.officialWebSiteURL && touched.officialWebSiteURL,
                  })}
                />
                {touched.officialWebSiteURL && errors.officialWebSiteURL && (
                  <p className="is-size-7 has-text-danger">
                    {errors.officialWebSiteURL}
                  </p>
                )}
              </div>
              <div className="field">
                <label className="label">サイト名(必須)</label>
                <Field
                  name="siteName"
                  value={values.siteName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="ネット手続き"
                  type="text"
                  className={classnames("input", {
                    "is-danger": errors.siteName && touched.siteName,
                  })}
                />
                {touched.siteName && errors.siteName && (
                  <p className="is-size-7 has-text-danger">{errors.siteName}</p>
                )}
              </div>
              <div className="field">
                <label className="label">ロゴのURL</label>
                <Field
                  name="logoURL"
                  value={values.logoURL}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="https://graffer.jp"
                  type="text"
                  className={classnames("input", {
                    "is-danger": errors.logoURL && touched.logoURL,
                  })}
                />
                {touched.logoURL && errors.logoURL && (
                  <p className="is-size-7 has-text-danger">{errors.logoURL}</p>
                )}
              </div>
            </section>
            <section className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={onModal}
              >
                キャンセル
              </button>
              <button
                type="submit"
                className={classnames("button is-info is-fullwidth", {
                  "is-loading": isLoading,
                })}
                disabled={!isValid}
              >
                {buttonText}
              </button>
            </section>
          </form>
        )}
      </Formik>
    </BaseModal>
  );
};
